<template>
  <div class="qr-wrap">
    <div class="qr-card" ref="card">
      <div class="qr-logo"><img :src="logoSrc" alt="AmejPay" /></div>

      <div class="qr-panel">
        <div v-if="isBuilding" class="qr-placeholder">
          <b-spinner label="Generating..."></b-spinner>
        </div>
        <div v-else-if="buildError" class="qr-placeholder is-error">{{ buildError }}</div>
        <img v-else :src="qrSrc" class="qr-img" :alt="name + ' payment QR code'" />
      </div>

      <div class="qr-name">{{ name || "—" }}</div>
      <div class="qr-email">{{ email || "—" }}</div>
      <div class="qr-rule"></div>
      <div class="qr-caption">{{ caption }}</div>
      <div class="qr-foot">AmejPay &middot; Fast &amp; Secure Payments</div>
    </div>

    <div class="qr-actions">
      <b-button variant="primary" :disabled="!ready || busy === 'download'" @click="download">
        <b-spinner small v-if="busy === 'download'" class="mr-1"></b-spinner>
        <span class="material-icons">download</span>
        <span>Download image</span>
      </b-button>

      <b-button
        variant="primary"
        v-if="canNativeShare"
        :disabled="!ready || busy === 'share'"
        @click="share"
      >
        <b-spinner small v-if="busy === 'share'" class="mr-1"></b-spinner>
        <span class="material-icons">ios_share</span>
        <span>Share</span>
      </b-button>

      <b-button variant="light" :disabled="!ready" @click="copyDetails">
        <span class="material-icons">content_copy</span>
        <span>{{ copied ? "Copied" : "Copy details" }}</span>
      </b-button>
    </div>

    <p class="qr-hint">
      <template v-if="canNativeShare">
        <strong>Share</strong> sends the image straight to WhatsApp, Gmail, Messenger or any
        app on this device.
      </template>
      <template v-else>
        Download the image, then attach it to an email, a chat or a social post. Sharing an
        image directly is only offered on devices that support it - on a desktop browser the
        file has to be attached by hand.
      </template>
    </p>

    <div class="qr-error" v-if="actionError">{{ actionError }}</div>

    <!-- Off-screen: the exact bitmap that gets downloaded and shared. -->
    <canvas ref="canvas" class="qr-canvas" :width="CARD_W" :height="CARD_H"></canvas>
  </div>
</template>

<script>
/**
 * The account QR card - the artwork, and the PNG behind Download and Share.
 *
 * The picture is drawn twice on purpose:
 *   - as HTML, so it inherits the app's styling and stays responsive; and
 *   - onto a <canvas>, which is what leaves the app as a file.
 *
 * Redrawing on canvas rather than screenshotting the DOM avoids adding html2canvas, and
 * gives a fixed 640x1000 image regardless of the visitor's screen, zoom or theme - a
 * screenshot of the DOM would vary with all three.
 */
import QRCode from "qrcode";
import logoSrc from "@/assets/amej-pay.png";
import { encode } from "@/utils/qrpayload";

const CARD_W = 640;
const CARD_H = 1000;
const FONT = '"Poppins", "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

export default {
  name: "QrCard",
  props: {
    payload: { type: Object, required: true },
    caption: { type: String, default: "Scan QR code to receive money" },
    fileName: { type: String, default: "amejpay-qr" },
    shareTitle: { type: String, default: "My AmejPay QR code" },
  },
  data() {
    return {
      CARD_W: CARD_W,
      CARD_H: CARD_H,
      logoSrc: logoSrc,
      qrSrc: "",
      isBuilding: true,
      buildError: "",
      actionError: "",
      busy: "",
      copied: false,
      copyTimer: null,
    };
  },
  computed: {
    name: function () { return (this.payload && this.payload.name) || ""; },
    email: function () { return (this.payload && this.payload.email) || ""; },
    encoded: function () { return encode(this.payload || {}); },
    ready: function () { return !!this.qrSrc && !this.buildError; },
    /**
     * Only true when the browser will actually accept a FILE. Chrome on desktop has
     * navigator.share but rejects files, so testing for `share` alone would offer a
     * button that always fails.
     */
    canNativeShare: function () {
      if (typeof navigator === "undefined" || !navigator) { return false; }
      return typeof navigator.share === "function" && typeof navigator.canShare === "function";
    },
  },
  watch: {
    // The profile arrives after the first render, so the QR is rebuilt when it changes.
    encoded: function () { this.build(); },
  },
  mounted() { this.build(); },
  beforeDestroy() {
    if (this.copyTimer) { clearTimeout(this.copyTimer); }
  },
  methods: {
    build() {
      const vm = this;
      this.buildError = "";
      // Nothing identifying yet - the profile call has not landed.
      if (!this.email && !this.name) { this.isBuilding = true; return; }
      this.isBuilding = true;
      return QRCode.toDataURL(this.encoded, {
        errorCorrectionLevel: "M",
        margin: 1,
        width: 640,
        color: { dark: "#111111", light: "#ffffff" },
      }).then(function (url) {
        vm.qrSrc = url;
        vm.isBuilding = false;
        return vm.paint();
      }).catch(function () {
        vm.isBuilding = false;
        vm.buildError = "The QR code could not be generated.";
      });
    },

    /** Loads an image URL, resolving to null instead of rejecting on failure. */
    loadImage(src) {
      return new Promise(function (resolve) {
        if (!src) { resolve(null); return; }
        const img = new Image();
        img.onload = function () { resolve(img); };
        img.onerror = function () { resolve(null); };
        img.src = src;
      });
    },

    roundRect(ctx, x, y, w, h, r) {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
    },

    /** Shrinks the font until the text fits, then clips with an ellipsis as a last resort. */
    fitText(ctx, text, maxWidth, startSize, weight) {
      let size = startSize;
      ctx.font = weight + " " + size + "px " + FONT;
      while (size > 12 && ctx.measureText(text).width > maxWidth) {
        size -= 1;
        ctx.font = weight + " " + size + "px " + FONT;
      }
      let out = text;
      if (ctx.measureText(out).width > maxWidth) {
        while (out.length > 1 && ctx.measureText(out + "...").width > maxWidth) {
          out = out.slice(0, -1);
        }
        out = out + "...";
      }
      return out;
    },

    paint() {
      const vm = this;
      const canvas = this.$refs.canvas;
      if (!canvas || !canvas.getContext) { return Promise.resolve(); }
      const ctx = canvas.getContext("2d");

      // Wait for webfonts, otherwise the first paint uses a fallback face and the
      // downloaded image does not match what is on screen.
      const fontsReady = (typeof document !== "undefined" && document.fonts && document.fonts.ready)
        ? document.fonts.ready.catch(function () { return null; })
        : Promise.resolve(null);

      return Promise.all([fontsReady, this.loadImage(this.qrSrc), this.loadImage(this.logoSrc)])
        .then(function (res) {
          const qrImg = res[1], logoImg = res[2];

          ctx.clearRect(0, 0, CARD_W, CARD_H);
          ctx.fillStyle = "#ffffff";
          vm.roundRect(ctx, 0, 0, CARD_W, CARD_H, 34);
          ctx.fill();

          // logo tile
          const tile = 158, tileX = (CARD_W - tile) / 2, tileY = 48;
          ctx.fillStyle = "#ffffff";
          ctx.strokeStyle = "#eef2f7";
          ctx.lineWidth = 2;
          vm.roundRect(ctx, tileX, tileY, tile, tile, 30);
          ctx.fill();
          ctx.stroke();
          if (logoImg && logoImg.width) {
            const pad = 26, box = tile - pad * 2;
            const scale = Math.min(box / logoImg.width, box / logoImg.height);
            const w = logoImg.width * scale, h = logoImg.height * scale;
            ctx.drawImage(logoImg, tileX + (tile - w) / 2, tileY + (tile - h) / 2, w, h);
          }

          // QR panel
          const panelX = 118, panelY = 248, panelW = CARD_W - panelX * 2, panelH = 410;
          ctx.fillStyle = "#ffffff";
          ctx.strokeStyle = "#e6eef6";
          ctx.lineWidth = 2;
          vm.roundRect(ctx, panelX, panelY, panelW, panelH, 22);
          ctx.fill();
          ctx.stroke();
          if (qrImg && qrImg.width) {
            const qs = 340;
            ctx.drawImage(qrImg, panelX + (panelW - qs) / 2, panelY + (panelH - qs) / 2, qs, qs);
          }

          const textMax = CARD_W - 88;
          ctx.textAlign = "center";
          ctx.textBaseline = "alphabetic";

          ctx.fillStyle = "#1f2937";
          const nameText = vm.fitText(ctx, vm.name || "-", textMax, 34, "700");
          ctx.fillText(nameText, CARD_W / 2, 724);

          ctx.fillStyle = "#6b7280";
          const mailText = vm.fitText(ctx, vm.email || "-", textMax, 23, "400");
          ctx.fillText(mailText, CARD_W / 2, 776);

          ctx.strokeStyle = "#dfe6ee";
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(CARD_W / 2 - 62, 826);
          ctx.lineTo(CARD_W / 2 + 62, 826);
          ctx.stroke();

          const pillW = CARD_W - 236, pillX = (CARD_W - pillW) / 2, pillY = 864, pillH = 66;
          ctx.fillStyle = "#eef2f7";
          vm.roundRect(ctx, pillX, pillY, pillW, pillH, 12);
          ctx.fill();
          ctx.fillStyle = "#3f4b5b";
          const capText = vm.fitText(ctx, vm.caption, pillW - 36, 23, "400");
          ctx.fillText(capText, CARD_W / 2, pillY + 42);

          ctx.fillStyle = "#9aa4ae";
          ctx.font = "400 19px " + FONT;
          ctx.fillText("AmejPay · Fast & Secure Payments", CARD_W / 2, 972);
        });
    },

    /** The canvas as a PNG Blob. */
    toBlob() {
      const canvas = this.$refs.canvas;
      return new Promise(function (resolve, reject) {
        if (!canvas) { reject(new Error("no canvas")); return; }
        if (canvas.toBlob) {
          canvas.toBlob(function (blob) {
            if (blob) { resolve(blob); } else { reject(new Error("toBlob returned null")); }
          }, "image/png");
          return;
        }
        // Safari <14 and friends.
        try {
          const parts = canvas.toDataURL("image/png").split(",");
          const bin = atob(parts[1]);
          const buf = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) { buf[i] = bin.charCodeAt(i); }
          resolve(new Blob([buf], { type: "image/png" }));
        } catch (e) { reject(e); }
      });
    },

    safeName() {
      const base = String(this.fileName || "amejpay-qr").replace(/[^\w.-]+/g, "-");
      return base.replace(/^-+|-+$/g, "") + ".png";
    },

    download() {
      const vm = this;
      this.actionError = "";
      this.busy = "download";
      return this.paint().then(function () { return vm.toBlob(); }).then(function (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = vm.safeName();
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Revoked on the next tick: revoking immediately cancels the download in Firefox.
        setTimeout(function () { URL.revokeObjectURL(url); }, 4000);
        vm.busy = "";
      }).catch(function () {
        vm.busy = "";
        vm.actionError = "The image could not be prepared for download.";
      });
    },

    share() {
      const vm = this;
      this.actionError = "";
      this.busy = "share";
      return this.paint().then(function () { return vm.toBlob(); }).then(function (blob) {
        const file = new File([blob], vm.safeName(), { type: "image/png" });
        const data = {
          files: [file],
          title: vm.shareTitle,
          text: vm.name ? ("Pay " + vm.name + " on AmejPay") : "My AmejPay QR code",
        };
        if (!navigator.canShare || !navigator.canShare(data)) {
          // This device cannot pass a file to the share sheet - give them the file instead.
          vm.busy = "";
          return vm.download();
        }
        return navigator.share(data).then(function () { vm.busy = ""; });
      }).catch(function (err) {
        vm.busy = "";
        // Dismissing the OS share sheet rejects with AbortError. That is not an error.
        if (err && err.name === "AbortError") { return; }
        vm.actionError = "Sharing is not available here. You can download the image instead.";
      });
    },

    copyDetails() {
      const vm = this;
      const text = [this.name, this.email].filter(function (v) { return !!v; }).join("\n");
      this.actionError = "";
      const done = function () {
        vm.copied = true;
        if (vm.copyTimer) { clearTimeout(vm.copyTimer); }
        vm.copyTimer = setTimeout(function () { vm.copied = false; }, 2000);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text).then(done).catch(function () {
          vm.actionError = "Could not copy. Your browser blocked clipboard access.";
        });
      }
      // Older browsers.
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        done();
      } catch (e) {
        this.actionError = "Could not copy on this browser.";
      }
    },
  },
};
</script>

<style scoped>
.qr-wrap { display: flex; flex-direction: column; align-items: center; }
.qr-card {
  width: 100%; max-width: 340px;
  background: #fff; border: 1px solid #eef2f7; border-radius: 18px;
  padding: 22px 20px 18px; text-align: center;
  box-shadow: 0 4px 20px rgba(17, 24, 39, 0.06);
}
.qr-logo {
  width: 84px; height: 84px; margin: 0 auto 16px;
  border: 1px solid #eef2f7; border-radius: 16px;
  display: flex; align-items: center; justify-content: center; padding: 12px;
}
.qr-logo img { max-width: 100%; max-height: 100%; }
.qr-panel {
  border: 1px solid #e6eef6; border-radius: 12px; padding: 16px;
  display: flex; align-items: center; justify-content: center;
  min-height: 226px; margin-bottom: 18px;
}
.qr-img { width: 100%; max-width: 210px; height: auto; display: block; }
.qr-placeholder { color: #8a94a6; font-size: 13px; }
.qr-placeholder.is-error { color: #dc3545; }
.qr-name { font-size: 19px; font-weight: 700; color: #1f2937; word-break: break-word; }
.qr-email { font-size: 13px; color: #6b7280; margin-top: 4px; word-break: break-all; }
.qr-rule { width: 64px; height: 1px; background: #dfe6ee; margin: 14px auto; }
.qr-caption {
  background: #eef2f7; border-radius: 8px; padding: 11px 12px;
  font-size: 13px; color: #3f4b5b;
}
.qr-foot { font-size: 11px; color: #9aa4ae; margin-top: 12px; }

.qr-actions {
  display: flex; flex-wrap: wrap; justify-content: center;
  gap: 10px; margin-top: 18px;
}
.qr-actions .btn { display: inline-flex; align-items: center; }
.qr-actions .material-icons { font-size: 18px; margin-right: 6px; }
.qr-hint {
  max-width: 420px; margin: 12px auto 0;
  font-size: 12px; color: #8a94a6; text-align: center;
}
.qr-error {
  margin-top: 10px; font-size: 13px; color: #b02a37;
  background: #fdeaea; border: 1px solid #f5c2c7; border-radius: 6px; padding: 8px 10px;
}
/* Present in the DOM (it is the source of the download) but never seen. */
.qr-canvas { position: absolute; left: -10000px; top: -10000px; width: 640px; height: 1000px; }
</style>
